import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "../config";
import { Search, ChevronLeft, ChevronRight, Loader2, XCircle, DollarSign, Calendar, Star } from "lucide-react";

interface PlatinumInvestment {
  _id: string;
  email: string;
  totalAmount: string;
  subscription: string;
  incomeDate: string;
  createdAt: string;
  isClosed: boolean;
}

const Platinum = () => {
  const [investmentData, setInvestmentData] = useState<PlatinumInvestment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [platinumDuration, setPlatinumDuration] = useState<number>(20);

  const fetchRules = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/business-rules`);
      if (response.data.status === 'success') {
        const durationRule = response.data.data.find((r: any) => r.key === 'PLATINUM_DURATION_MONTHS');
        if (durationRule) setPlatinumDuration(Number(durationRule.value));
      }
    } catch (error) {
      console.error("Error fetching rules:", error);
    }
  };

  const [debitValues, setDebitValues] = useState<Record<string, string>>({});
  const [creditValues, setCreditValues] = useState<Record<string, string>>({});

  const fetchInvestments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/api/platinum/admin/investors/paginated`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          search: searchTerm
        }
      });

      if (response.data.status === "success") {
        setInvestmentData(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          totalPages: response.data.pagination.totalPages
        }));
      }
    } catch (error) {
      console.error("Error fetching platinum investments:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      fetchInvestments();
    }, 500);
    fetchRules();
    return () => clearTimeout(delaySearch);
  }, [fetchInvestments]);

  const handleTransaction = async (email: string, amount: string, incomeDate: string, type: "debit" | "credit") => {
    if (!amount || isNaN(Number(amount))) return;
    try {
      const amountNum = parseFloat(amount);
      await axios.post(`${API_BASE}/api/platinum/update`, {
        email,
        incomeDate,
        amount: amountNum.toFixed(2),
        type,
        subscription: "Platinum",
        totalAmount: (Number(investmentData.find(u => u.email === email)?.totalAmount || 0) + (type === 'credit' ? amountNum : -amountNum)).toFixed(2)
      });

      fetchInvestments();
      if (type === "debit") setDebitValues(prev => ({ ...prev, [email]: "" }));
      else setCreditValues(prev => ({ ...prev, [email]: "" }));

      alert(`Successfully processed ${type} of $${amountNum.toFixed(2)} for ${email}`);
    } catch (error) {
      console.error(`Error processing ${type}:`, error);
      alert("Failed to process transaction");
    }
  };

  const handleCloseInvestment = async (email: string) => {
    if (!window.confirm(`Are you sure you want to close platinum investment for ${email}?`)) return;
    try {
      await axios.post(`${API_BASE}/api/platinum/close/${email}`);
      fetchInvestments();
      alert("Platinum investment closed successfully");
    } catch (error) {
      console.error("Error closing platinum investment:", error);
      alert("Failed to close investment");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 bg-gray-50 min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 tracking-tight flex items-center gap-3">
            {/* <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" /> */}
            Platinum Investments
          </h1>
          <p className="text-gray-500 mt-1">High-yield premium investment portfolios</p>
        </div>

        <div className="relative w-full md:w-96">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by email..."
            className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all outline-none"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
          />
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Loader2 className="w-10 h-10 text-yellow-500 animate-spin" />
            <p className="text-gray-500 font-medium mt-4">Fetching premium accounts...</p>
          </div>
        ) : investmentData.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200">
            <Search className="w-12 h-12 text-gray-400 mb-4" />
            <p className="text-gray-500 font-medium">No platinum accounts found</p>
          </div>
        ) : (
          investmentData.map((invest) => {
            // Calculate Closing Date (Example: 20 months like in screenshot logic seem to imply, or just use a placeholder logic if exact calculation not known, but can infer from screenshot dates 29/01/2026 to 29/09/2027 is 20 months)
            const investDate = new Date(invest.createdAt);
            const closingDate = new Date(investDate);
            closingDate.setMonth(closingDate.getMonth() + platinumDuration);

            return (
              <div key={invest._id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 transition-all">
                {/* Header */}
                <div className="flex justify-between items-start gap-4 mb-4">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 truncate">{invest.email}</h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-blue-600 font-bold uppercase tracking-tight">Platinum Plan</span>
                      <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
                      <span className="text-[10px] text-green-600 font-bold uppercase">Active</span>
                    </div>
                  </div>
                </div>

                {/* Detail Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-3 gap-x-6 text-[11px] mb-4">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Start Date</span>
                    <span className="text-gray-700 font-medium">{new Date(invest.createdAt).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Closing Date</span>
                    <span className="text-gray-700 font-medium">{closingDate.toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Next Income</span>
                    <span className="text-gray-700 font-medium">{new Date(invest.incomeDate).toLocaleDateString('en-GB')}</span>
                  </div>
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-400 font-bold uppercase text-[9px]">Total Amount</span>
                    <span className="text-blue-600 font-black text-sm md:text-base">${parseFloat(invest.totalAmount).toLocaleString()}</span>
                  </div>
                </div>

                {/* Actions Row */}
                <div className="space-y-3 pt-3 border-t border-gray-50">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {/* Debit Group */}
                    <div className="flex items-center gap-1.5 w-full">
                      <div className="relative flex-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] font-bold">$</span>
                        <input
                          type="text"
                          placeholder="Debit"
                          className="w-full pl-5 pr-2 py-1.5 bg-gray-50 border border-gray-100 rounded-lg text-xs focus:ring-1 focus:ring-red-400 outline-none"
                          value={debitValues[invest.email] || ""}
                          onChange={(e) => setDebitValues(prev => ({ ...prev, [invest.email]: e.target.value }))}
                        />
                      </div>
                      <button
                        onClick={() => handleTransaction(invest.email, debitValues[invest.email], invest.incomeDate, "debit")}
                        className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold shadow-sm whitespace-nowrap"
                      >
                        Debit
                      </button>
                    </div>

                    {/* Credit Group */}
                    <div className="flex items-center gap-1.5 w-full">
                      <div className="relative flex-1">
                        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] font-bold">$</span>
                        <input
                          type="text"
                          placeholder="Credit"
                          className="w-full pl-5 pr-2 py-1.5 bg-gray-50 border border-gray-100 rounded-lg text-xs focus:ring-1 focus:ring-green-400 outline-none"
                          value={creditValues[invest.email] || ""}
                          onChange={(e) => setCreditValues(prev => ({ ...prev, [invest.email]: e.target.value }))}
                        />
                      </div>
                      <button
                        onClick={() => handleTransaction(invest.email, creditValues[invest.email], invest.incomeDate, "credit")}
                        className="bg-green-500 text-white px-3 py-1.5 rounded-lg text-[10px] font-bold shadow-sm whitespace-nowrap"
                      >
                        Credit
                      </button>
                    </div>
                  </div>

                  <button
                    onClick={() => handleCloseInvestment(invest.email)}
                    className="w-full py-2 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  >
                    Close Portfolio Account
                  </button>
                </div>
              </div>
            );
          })
        )}

        {/* Pagination */}
        {!loading && investmentData.length > 0 && (
          <div className="p-4 md:px-6 md:py-4 mt-4 flex flex-col md:flex-row items-center justify-between gap-4 bg-white rounded-lg shadow-sm border border-gray-200">
            <p className="text-sm text-gray-500 order-2 md:order-1">
              Premium Users: <span className="font-bold text-gray-900">{pagination.total}</span> total
            </p>

            <div className="flex items-center gap-2 order-1 md:order-2">
              <span className="text-sm text-gray-600 hidden sm:inline">Show:</span>
              <select
                value={pagination.limit}
                onChange={(e) => {
                  const newLimit = parseInt(e.target.value, 10);
                  setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
              >
                <option value="20">20 / page</option>
                <option value="50">50 / page</option>
                <option value="100">100 / page</option>
              </select>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              <div className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-xs md:text-sm font-bold shadow-sm whitespace-nowrap">
                Page {pagination.page} of {pagination.totalPages}
              </div>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(pagination.totalPages, prev.page + 1) }))}
                disabled={pagination.page === pagination.totalPages}
                className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Platinum;
